import { User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { analytics } from "@/hooks/useAnalytics";
import { clearAllCaches } from "@/services/templateService";

/**
 * User Session Manager
 *
 * Coordinates complete user session lifecycle management including:
 * - User login/logout
 * - User switching detection
 * - Cache management
 * - Storage cleanup
 * - Analytics reset
 */

export interface SessionContext {
  userId: string | null;
  organizationId: string | null;
  sessionTimestamp: number;
}

export interface CleanupResult {
  queryCache: boolean;
  serviceCaches: boolean;
  localStorage: boolean;
  sessionStorage: boolean;
  analytics: boolean;
  errors: string[];
}

class UserSessionManager {
  private currentSession: SessionContext | null = null;
  private readonly SESSION_STORAGE_KEY = "current_user_session";

  // Categories of data that need different cleanup strategies
  private readonly USER_SCOPED_QUERY_PATTERNS = [
    "userProfile",
    "conversations",
    "agents",
    "knowledge",
    "behaviors",
    "inbox",
    "admin",
    "messages",
    "chat",
    "voice",
    "api",
  ];

  private readonly USER_SCOPED_STORAGE_PATTERNS = [
    "agent_chat_user_",
    "public_chat_",
    "redirectAfterAuth",
    "pendingNewUserRedirect",
    "registrationInProgress",
    "auth-temporary-session",
    "inbox_conversations_cache",
    "GLOBAL_PRE_SESSION_FIELDS",
    "DIRECT_PRE_SESSION_FIELDS",
    "conversation_",
    "messages_",
    "chat_",
    "agent_",
    "sb-", // Supabase auth tokens
    "supabase.auth.token", // Legacy Supabase tokens
  ];

  private readonly GLOBAL_STORAGE_PATTERNS = [
    "offerEndTime", // Can persist across users
    // Remove onboarding_visited_ to clear it on logout
  ];

  /**
   * Initialize or update current session
   */
  setCurrentSession(user: User | null, organizationId: string | null): boolean {
    const newSession: SessionContext = {
      userId: user?.id || null,
      organizationId,
      sessionTimestamp: Date.now(),
    };

    const previousSession = this.getCurrentSession();
    const isUserChange = this.isUserChange(previousSession, newSession);

    // Store current session
    this.currentSession = newSession;
    try {
      sessionStorage.setItem(
        this.SESSION_STORAGE_KEY,
        JSON.stringify(newSession),
      );
    } catch (error) {
      console.warn(
        "[SessionManager] Failed to store session in sessionStorage:",
        error,
      );
    }

    console.log("[SessionManager] Session updated:", {
      previousUserId: previousSession?.userId,
      currentUserId: newSession.userId,
      isUserChange,
      timestamp: newSession.sessionTimestamp,
    });

    return isUserChange;
  }

  /**
   * Get current session context
   */
  getCurrentSession(): SessionContext | null {
    if (this.currentSession) {
      return this.currentSession;
    }

    // Try to restore from sessionStorage
    try {
      const stored = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (stored) {
        this.currentSession = JSON.parse(stored);
        return this.currentSession;
      }
    } catch (error) {
      console.warn(
        "[SessionManager] Failed to restore session from sessionStorage:",
        error,
      );
    }

    return null;
  }

  /**
   * Detect if this is a user change (different user, not just login/logout)
   */
  private isUserChange(
    previous: SessionContext | null,
    current: SessionContext,
  ): boolean {
    // If no previous session, this is not a user change (initial login)
    if (!previous) {
      return false;
    }

    // If previous had a user and current has a different user, it's a user change
    if (
      previous.userId &&
      current.userId &&
      previous.userId !== current.userId
    ) {
      return true;
    }

    // If previous had a user and current has no user, it's logout (not user change)
    if (previous.userId && !current.userId) {
      return false;
    }

    // If previous had no user and current has a user, it's login (not user change)
    if (!previous.userId && current.userId) {
      return false;
    }

    return false;
  }

  /**
   * Perform comprehensive logout cleanup
   */
  async performLogout(queryClient?: any): Promise<CleanupResult> {
    console.log(
      "[SessionManager] Starting AGGRESSIVE comprehensive logout cleanup",
    );

    const result: CleanupResult = {
      queryCache: false,
      serviceCaches: false,
      localStorage: false,
      sessionStorage: false,
      analytics: false,
      errors: [],
    };

    try {
      // 1. Clear ALL React Query cache (not just user-scoped)
      if (queryClient) {
        console.log("[SessionManager] Clearing ALL React Query cache");
        queryClient.clear(); // Nuclear option - clear everything
        result.queryCache = true;
      }
    } catch (error) {
      result.errors.push(`Query cache cleanup failed: ${error}`);
    }

    try {
      // 2. Clear service caches
      clearAllCaches();
      result.serviceCaches = true;
    } catch (error) {
      result.errors.push(`Service cache cleanup failed: ${error}`);
    }

    try {
      // 3. AGGRESSIVE localStorage cleanup - clear almost everything
      console.log("[SessionManager] AGGRESSIVE localStorage cleanup");
      const keysToKeep = ["offerEndTime"]; // Only keep absolutely necessary items
      const allKeys: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) allKeys.push(key);
      }

      allKeys.forEach((key) => {
        if (!keysToKeep.includes(key)) {
          console.log("[SessionManager] Removing localStorage key:", key);
          localStorage.removeItem(key);
        }
      });

      result.localStorage = true;
    } catch (error) {
      result.errors.push(`LocalStorage cleanup failed: ${error}`);
    }

    try {
      // 4. AGGRESSIVE sessionStorage cleanup - clear EVERYTHING
      console.log(
        "[SessionManager] AGGRESSIVE sessionStorage cleanup - clearing ALL",
      );
      sessionStorage.clear(); // Nuclear option - clear everything
      result.sessionStorage = true;
    } catch (error) {
      result.errors.push(`SessionStorage cleanup failed: ${error}`);
    }

    try {
      // 5. Reset analytics
      analytics.reset();
      result.analytics = true;
    } catch (error) {
      result.errors.push(`Analytics reset failed: ${error}`);
    }

    try {
      // 6. Clear all cookies (browser cookies that we can access)
      this.clearAllAccessibleCookies();
    } catch (error) {
      result.errors.push(`Cookie cleanup failed: ${error}`);
    }

    // 7. Clear current session
    this.currentSession = null;

    // 8. Force page reload after a short delay to ensure clean state
    setTimeout(() => {
      console.log("[SessionManager] Forcing page reload for clean state");
      window.location.href = "/login";
    }, 100);

    console.log(
      "[SessionManager] AGGRESSIVE logout cleanup completed:",
      result,
    );
    return result;
  }

  /**
   * Perform user change cleanup (when switching users)
   */
  async performUserChangeCleanup(queryClient?: any): Promise<CleanupResult> {
    console.log("[SessionManager] Starting user change cleanup");

    // User change cleanup is similar to logout but more aggressive
    const result = await this.performLogout(queryClient);

    // Additional cleanup for user changes
    try {
      // Clear any remaining caches that might persist between sessions
      this.clearUserChangeSpecificData();
    } catch (error) {
      result.errors.push(`User change specific cleanup failed: ${error}`);
    }

    console.log("[SessionManager] User change cleanup completed:", result);
    return result;
  }

  /**
   * Clear React Query cache for user-scoped data
   */
  private async clearUserScopedQueries(queryClient: any): Promise<void> {
    console.log("[SessionManager] Clearing user-scoped React Query cache");

    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();

    for (const query of queries) {
      const queryKey = query.queryKey;

      // Check if this query should be cleared (user-scoped)
      if (this.isUserScopedQuery(queryKey)) {
        console.log("[SessionManager] Removing query:", queryKey);
        queryClient.removeQueries({ queryKey });
      }
    }

    // Also invalidate remaining queries to force fresh data
    await queryClient.invalidateQueries();
  }

  /**
   * Check if a query key represents user-scoped data
   */
  private isUserScopedQuery(queryKey: unknown[]): boolean {
    if (!Array.isArray(queryKey) || queryKey.length === 0) {
      return false;
    }

    const firstKey = String(queryKey[0]).toLowerCase();

    return this.USER_SCOPED_QUERY_PATTERNS.some((pattern) =>
      firstKey.includes(pattern.toLowerCase()),
    );
  }

  /**
   * Clear user-scoped localStorage data
   */
  private clearUserScopedStorage(): void {
    console.log("[SessionManager] Clearing user-scoped localStorage");

    const keysToRemove: string[] = [];

    // Identify keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isUserScopedStorageKey(key)) {
        keysToRemove.push(key);
      }
    }

    // Remove identified keys
    keysToRemove.forEach((key) => {
      console.log("[SessionManager] Removing localStorage key:", key);
      localStorage.removeItem(key);
    });
  }

  /**
   * Clear user-scoped sessionStorage data
   */
  private clearUserScopedSessionStorage(): void {
    console.log("[SessionManager] Clearing user-scoped sessionStorage");

    const keysToRemove: string[] = [];

    // Identify keys to remove
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && this.isUserScopedStorageKey(key)) {
        keysToRemove.push(key);
      }
    }

    // Remove identified keys
    keysToRemove.forEach((key) => {
      console.log("[SessionManager] Removing sessionStorage key:", key);
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Check if a storage key is user-scoped
   */
  private isUserScopedStorageKey(key: string): boolean {
    // Check against user-scoped patterns
    const isUserScoped = this.USER_SCOPED_STORAGE_PATTERNS.some((pattern) =>
      key.includes(pattern),
    );

    // Check against global patterns (should NOT be removed)
    const isGlobal = this.GLOBAL_STORAGE_PATTERNS.some((pattern) =>
      key.includes(pattern),
    );

    return isUserScoped && !isGlobal;
  }

  /**
   * Public method to check if a storage key is user-scoped
   * Used by testing utilities and other modules
   */
  public isUserScopedKey(key: string): boolean {
    return this.isUserScopedStorageKey(key);
  }

  /**
   * Clear additional data specific to user changes
   */
  private clearUserChangeSpecificData(): void {
    console.log("[SessionManager] Clearing user change specific data");

    // Clear any window-level global variables
    if (typeof window !== "undefined") {
      // Clear any global variables that might persist
      (window as any).GLOBAL_PRE_SESSION_FIELDS = undefined;
    }

    // Clear any module-level state that might persist
    // This is where we'd add any additional cleanup for specific modules
  }

  /**
   * Clear all accessible cookies
   */
  private clearAllAccessibleCookies(): void {
    console.log("[SessionManager] Clearing all accessible cookies");

    // Get all cookies for the current domain
    const cookies = document.cookie.split(";");

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (name) {
        // Clear cookie for current path
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

        // Clear cookie for current domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;

        // Clear cookie for parent domain (if subdomain)
        const domainParts = window.location.hostname.split(".");
        if (domainParts.length > 2) {
          const parentDomain = domainParts.slice(-2).join(".");
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${parentDomain}`;
        }

        console.log("[SessionManager] Cleared cookie:", name);
      }
    });
  }

  /**
   * Get debugging information about current session state
   */
  getDebugInfo(): {
    currentSession: SessionContext | null;
    localStorageKeys: string[];
    sessionStorageKeys: string[];
    userScopedLocalStorage: string[];
    userScopedSessionStorage: string[];
  } {
    const localStorageKeys: string[] = [];
    const sessionStorageKeys: string[] = [];
    const userScopedLocalStorage: string[] = [];
    const userScopedSessionStorage: string[] = [];

    // Collect localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        localStorageKeys.push(key);
        if (this.isUserScopedStorageKey(key)) {
          userScopedLocalStorage.push(key);
        }
      }
    }

    // Collect sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        sessionStorageKeys.push(key);
        if (this.isUserScopedStorageKey(key)) {
          userScopedSessionStorage.push(key);
        }
      }
    }

    return {
      currentSession: this.getCurrentSession(),
      localStorageKeys,
      sessionStorageKeys,
      userScopedLocalStorage,
      userScopedSessionStorage,
    };
  }
}

// Export singleton instance
export const userSessionManager = new UserSessionManager();
